import React, { useCallback, useEffect, useState } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import {
  createEnterpriseApiKey,
  fetchEnterpriseApiKeys,
  fetchEnterpriseMcpImportBundle,
  revokeEnterpriseApiKey,
} from '../../api/enterprises';

function downloadJsonFile(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function EnterpriseApiKeysPanel({ enterpriseId }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('Intégration MCP');
  const [authorEmail, setAuthorEmail] = useState('');
  const [newKey, setNewKey] = useState('');
  const [creating, setCreating] = useState(false);
  const [downloadingMcp, setDownloadingMcp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchEnterpriseApiKeys(enterpriseId);
      setKeys(res?.data?.keys || []);
    } catch (e) {
      setError(e?.message || 'Impossible de charger les clés.');
    } finally {
      setLoading(false);
    }
  }, [enterpriseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setNewKey('');
    try {
      const res = await createEnterpriseApiKey(enterpriseId, {
        name: name.trim() || 'Clé API',
        authorEmail: authorEmail.trim() || undefined,
      });
      setNewKey(res?.data?.apiKey || '');
      await load();
    } catch (e) {
      setError(e?.message || 'Création impossible.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('Révoquer cette clé ? Les intégrations qui l’utilisent cesseront de fonctionner.')) {
      return;
    }
    setError('');
    try {
      await revokeEnterpriseApiKey(enterpriseId, keyId);
      await load();
    } catch (e) {
      setError(e?.message || 'Révocation impossible.');
    }
  };

  const handleDownloadMcp = async ({ useExistingKey } = {}) => {
    setDownloadingMcp(true);
    setError('');
    try {
      const payload = {
        name: name.trim() || 'Import MCP',
        authorEmail: authorEmail.trim() || undefined,
      };
      if (useExistingKey && newKey) {
        payload.apiKey = newKey;
      }
      const res = await fetchEnterpriseMcpImportBundle(enterpriseId, payload);
      const bundle = res?.data?.bundle;
      if (!bundle?.mcpServers) {
        throw new Error('Réponse MCP invalide.');
      }
      if (res?.data?.apiKey) {
        setNewKey(res.data.apiKey);
        await load();
      }
      downloadJsonFile('hubup-enterprise-mcp.json', bundle);
    } catch (e) {
      setError(e?.message || 'Téléchargement impossible.');
    } finally {
      setDownloadingMcp(false);
    }
  };

  return (
    <section className="rounded-lg border border-border p-4 space-y-4">
      <div>
        <h2 className="text-sm font-medium text-foreground">Clé API & MCP</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Téléchargez le MCP pour connecter nos outils HubUP et CRM à vos assistants IA.
        </p>
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <div className="rounded-md border border-border p-3 space-y-3 bg-muted/30">
        <p className="text-xs font-medium text-foreground">Fichier MCP</p>
        <p className="text-xs text-muted-foreground">
          Importez le fichier dans votre assistant pour lui donner accès à votre espace entreprise.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            disabled={downloadingMcp}
            onClick={() => void handleDownloadMcp({ useExistingKey: false })}
          >
            {downloadingMcp ? 'Préparation…' : 'Télécharger le MCP'}
          </Button>
          {newKey ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={downloadingMcp}
              onClick={() => void handleDownloadMcp({ useExistingKey: true })}
            >
              Télécharger avec la clé affichée ci-dessous
            </Button>
          ) : null}
        </div>
      </div>

      {newKey ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
          <p className="text-xs font-medium text-foreground">Copiez la clé maintenant (affichée une seule fois)</p>
          <code className="block text-xs break-all font-mono bg-background/80 p-2 rounded border border-border">
            {newKey}
          </code>
        </div>
      ) : null}

      <form onSubmit={handleCreate} className="space-y-3">
        <Input label="Nom de la clé" value={name} onChange={(e) => setName(e.target.value)} />
        <Input
          label="E-mail auteur des notifications HubUP"
          type="email"
          value={authorEmail}
          onChange={(e) => setAuthorEmail(e.target.value)}
          placeholder="Optionnel — défaut : votre e-mail"
        />
        <Button type="submit" size="sm" disabled={creating}>
          {creating ? 'Création…' : 'Générer une clé seule'}
        </Button>
      </form>

      {loading ? (
        <p className="text-xs text-muted-foreground">Chargement…</p>
      ) : keys.length === 0 ? (
        <p className="text-xs text-muted-foreground">Aucune clé active.</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {keys.map((k) => (
            <li key={k._id} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
              <div className="min-w-0">
                <p className="font-medium truncate">{k.name}</p>
                <p className="text-xs text-muted-foreground font-mono">{k.keyPrefix}…</p>
                <p className="text-xs text-muted-foreground">{k.authorEmail}</p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => void handleRevoke(k._id)}>
                Révoquer
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
